import {FC} from 'react'
import {Field, ErrorMessage, FormikValues, Formik, Form, FieldMetaProps} from 'formik'
import {ListLoading} from './ListLoading'
import {ID, isNotEmpty} from '../_metronic/helpers'
// import { FileUpload, Switch } from './CustomInput'
import {types} from './SelectFile'
import {uploadFile} from './fileUtils'
import {buildValidationSchema} from './validationSchema'
import TiptapEditor from './TiptapEditor'
import AdditionalFieldsEditor from './AdditionalFieldsEditor'

type OptionsQuery = {
  queryKey: string[]
  enabled: boolean
  name?: string
}

export type FieldProps = {
  name: string
  label?: string
  required?: boolean //default is false
  full?: boolean //default is false
  disabled?: boolean //default is false
  hidden?: boolean
  type?: //default is text
  | 'time'
    | 'text'
    | 'date'
    | 'numeric'
    | 'email'
    | 'password'
    | 'textarea'
    | 'select'
    | 'switch'
    | 'file'
    | 'files'
    | 'multi-select'
    | 'multi-input'
    | 'checkbox'
    | 'image'
    | 'useremail'
    | 'link'
    | 'user_info'
    | 'content'
    | 'key-value'
    | 'additional-fields'
  fileProps?: {
    limit: number
  }
  options?: {label: string; value: any}[]
  optionsQuery?: OptionsQuery
  searchFn?: (val: string) => void
  createFn?: (val: string) => void
  selectFn?: (val: string | string[]) => void
  meta?: FieldMetaProps<any>
  limit?: number
  acceptedTypes?: (keyof typeof types)[]
  truthValue?: string
  falseValue?: string
  view?: boolean //default is true
  edit?: boolean //default is true
  create?: boolean //default is true
  table?: boolean //default is false
  sortable?: boolean //default is false
  sortField?: string
  viewName?: string
  createField?: string
  fields?: any[]
  initialValue?: any // default for date is new Date(), for switch is true, for text is ''
  checkboxLabel?: string
  onchecked?: (e: React.ChangeEvent<HTMLInputElement>, formik: any) => void
}

export type Props = {
  isLoading: boolean
  data: any
  update?: (state: any) => Promise<any>
  create?: (state: any) => Promise<any>
  id?: ID | undefined
  setItemIdForUpdate?: (id: ID) => void
  refetch?: () => void
  schema?: any
  fields: FieldProps[] | ((options: {values: any}) => FieldProps[])
  onSuccess?: () => void
}

export const CForm: FC<Props> = (props) => {
  ///getting model from url which will be used as upload folder
  const getModelFromUrl = () => {
    const path = window.location.pathname
    const parts = path.split('/').filter(Boolean)
    return parts.includes('edit') || parts.includes('create')
      ? parts[parts.length - 2]
      : parts[parts.length - 1]
  }
  const modelName = getModelFromUrl()

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) props.refetch?.()
    props.setItemIdForUpdate && props.setItemIdForUpdate(undefined)
  }
  async function processFile(obj: any) {
    for (const key in obj) {
      const value = obj[key]
      if (value instanceof File) {
        const uploaded = await uploadFile(value, modelName)
        const FieldName = key.replace('_to_upload', '')
        obj[FieldName] = uploaded
        delete obj[key]
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        processFile(value)
      }
    }
  }
  const submit = async (values: any, {setSubmitting}: Partial<FormikValues>) => {
    setSubmitting(true)
    try {
      await processFile(values)
      if (isNotEmpty(values.id)) await props.update?.(values)
      else await props.create?.(values)
      if (props.onSuccess) {
        props.onSuccess()
      } else {
        cancel(true)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setSubmitting?.(false)
    }
  }

  const {schema, initialValues} = buildValidationSchema(props.fields, props.data)

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={schema}
      initialValues={{...initialValues, ...props.data}}
      onSubmit={submit}
    >
      {(formik) => {
        const isEditing = isNotEmpty(formik.values?.id)
        const fields = (
          typeof props.fields === 'function' ? props.fields({values: formik.values}) : props.fields
        )?.filter((f: any) => (isEditing ? f.edit !== false : f.create !== false))
        return (
          <>
            <Form className='space-y-8'>
              <div
                className='flex flex-col overflow-y-auto pe-7 -me-7 max-h-[calc(100vh-300px)]'
                id='kt_modal_add_academic_year_scroll'
                data-kt-scroll='true'
                data-kt-scroll-activate='{default: false, lg: true}'
                data-kt-scroll-max-height='auto'
                data-kt-scroll-offset='300px'
              ></div>

              <div className='grid grid-cols-12 gap-4'>
                {fields?.map((field: any, index: any) => (
                  <CFields key={index} field={field} formik={formik} />
                ))}
              </div>

              {/* Validation Summary for Debugging */}
              {Object.keys(formik.errors).length > 0 && formik.dirty && (
                <div className='p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50' role='alert'>
                  <div className='font-medium text-red-700'>Please fix the following issues:</div>
                  <ul className='mt-1.5 list-disc list-inside text-red-600'>
                    {Object.entries(formik.errors).map(([key, error]) => (
                      <li key={key}>
                        <strong>{key}</strong>:{' '}
                        {typeof error === 'string' ? error : 'Check this section'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className='text-center pt-10 flex flex-wrap justify-center gap-3'>
                <button
                  type='reset'
                  onClick={() => cancel()}
                  className='inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition'
                  disabled={formik.isSubmitting || props.isLoading}
                >
                  Discard
                </button>
                <button
                  type='submit'
                  disabled={props.isLoading || formik.isSubmitting || !formik.isValid}
                  data-kt-academic_years-modal-action='submit'
                  data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}
                  className={`
    relative inline-flex items-center justify-center px-6 py-2
    rounded-md font-medium text-white transition-colors
   bg-primary hover:bg-teal-700
    disabled:opacity-50 disabled:cursor-not-allowed
  `}
                >
                  <span className={formik.isSubmitting ? 'invisible' : ''}>Submit</span>

                  {formik.isSubmitting && (
                    <span className='flex items-center justify-center gap-2'>
                      <span>Please wait...</span>
                      <svg
                        className='w-5 h-5 animate-spin text-white'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                        ></path>
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </Form>

            {(formik.isSubmitting || props.isLoading) && <ListLoading />}
          </>
        )
      }}
    </Formik>
  )
}

export const CFields: FC<{field: FieldProps; formik: any}> = ({field, formik}) => {
  return (
    <div className={`mb-5 ${!field.full ? 'lg:col-span-6' : 'lg:col-span-12'}`}>
      <label
        className={`block mb-3 text-sm font-medium ${
          field.required ? 'after:content-["*"] after:ml-1 after:text-red-500' : ''
        }`}
      >
        {field.label}
      </label>
      {
        // field.type === 'file' ? (
        //   <FileUpload field={field} form={formik} />
        // ) :
        field.type === 'content' ? (
          <TiptapEditor
            value={formik.values[field.name]}
            onChange={(html: string) => formik.setFieldValue(field.name, html)}
          />
        ) : field.type === 'additional-fields' ? (
          <AdditionalFieldsEditor
            value={formik.values[field.name] || []}
            onChange={(val: any) => formik.setFieldValue(field.name, val)}
          />
        ) : field.type === 'select' ? (
          <Field
            as='select'
            className='block w-full text-base py-2 px-4 bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500'
            name={field.name}
            disabled={field.disabled}
          >
            <option value=''>Select {field.label}</option>
            {field.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        ) : (
          <Field
            type={field.type}
            className='block w-full text-base py-2 px-4 bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500'
            name={field.name}
            as={field.type === 'textarea' ? 'textarea' : undefined}
            // component={field.type === 'switch' ? Switch : undefined}
            disabled={field.disabled}
          />
        )
      }
      <div className='text-red-600 mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  )
}
